import type { Difficulty, GeneratedQuestion, UasaFormatRules, ValidationIssue } from "./uasa.js";
import { expectedDifficultyCounts, validateOfficialPaper } from "./uasa.js";

/**
 * Practice topic names for Matematik Tahun 4–6 (KSSR). These are broad topic labels only —
 * NOT official SK/SP codes. Mapping to DSKP standards happens when verified curriculum
 * documents are loaded into curriculum_versions / curriculum_standards (Handbook §G).
 */
export const MATH_TOPICS: Readonly<Record<4 | 5 | 6, readonly string[]>> = {
  4: ["Nombor Bulat dan Operasi", "Pecahan", "Perpuluhan", "Peratus", "Wang", "Masa dan Waktu", "Ukuran dan Sukatan", "Ruang", "Koordinat, Nisbah dan Kadaran", "Pengurusan Data"],
  5: ["Nombor Bulat dan Operasi", "Pecahan", "Perpuluhan", "Peratus", "Wang", "Masa dan Waktu", "Ukuran dan Sukatan", "Ruang", "Koordinat, Nisbah dan Kadaran", "Pengurusan Data"],
  6: ["Nombor Bulat dan Operasi", "Pecahan", "Perpuluhan", "Peratus", "Wang", "Masa dan Waktu", "Ukuran dan Sukatan", "Ruang", "Koordinat, Nisbah dan Kadaran", "Pengurusan Data", "Kebolehjadian"],
};

/** official/custom = UASA Matematik T4–6; dskp = practice built from imported DSKP standards (any subject/year). */
export type PaperMode = "official" | "custom" | "dskp" | "uasa";
/** dlp = BM + English side by side; en = English only (Bahasa Inggeris, DLP Maths/Science in English). */
export type PaperLanguage = "ms" | "en" | "dlp";
export type QuestionType = "objektif" | "subjektif";

export interface PaperSettings {
  year: number;
  mode: PaperMode;
  topics: string[];
  language: PaperLanguage;
  /** Custom and dskp modes. */
  questionCount?: number;
  /** Custom and dskp modes; defaults to 5:3:2. */
  difficultyRatio?: [number, number, number];
  /** dskp mode only. */
  subject?: string;
  level?: "rendah" | "menengah";
  versionId?: string;
  spCodes?: string[];
  /** uasa mode: DSKP unit titles to cover (all units when absent). */
  units?: string[];
  /** uasa mode: Kertas 1 or 2 (language subjects). */
  paper?: 1 | 2;
  /** dskp mode: "uasa" = written and rendered in the UASA paper style (all new papers). */
  format?: "uasa";
}

/** One Standard Pembelajaran as stored in curriculum_standards (never typed by hand). */
export interface CurriculumStandard {
  note?: string | null;
  spCode: string;
  spDesc: string;
  skCode: string;
  skDesc: string | null;
  topic: string | null;
  theme: string | null;
}

export interface BlueprintSlot {
  section: "A" | "B";
  number: number;
  marks: number;
  difficulty: Difficulty;
  topic: string;
  /** dskp mode: the SP this question must assess, and its format. */
  spCode?: string;
  type?: QuestionType;
}

export interface PaperBlueprint {
  title: string;
  totalMarks: number;
  durationMinutes: number | null;
  slots: BlueprintSlot[];
  /** dskp mode: subject slug and the selected standards (for the prompt and the answer scheme). */
  subject?: string;
  standards?: CurriculumStandard[];
}

/** Deterministic plan the AI must fill: which section/number/marks/difficulty/topic each question has. */
export function buildBlueprint(s: PaperSettings, rules: UasaFormatRules): PaperBlueprint {
  if (s.mode === "dskp") throw new RangeError("Guna buildPracticeBlueprint untuk mod DSKP");
  if (s.topics.length === 0) throw new RangeError("Pilih sekurang-kurangnya satu topik");
  const topicAt = (i: number) => s.topics[i % s.topics.length]!;
  const slots: PaperBlueprint["slots"] = [];
  if (s.mode === "official") {
    const n = rules.sections.reduce((a, x) => a + x.questionCount, 0);
    const [lo, mid, hi] = expectedDifficultyCounts(rules.difficultyRatio, n);
    const diffs: Difficulty[] = [...Array(lo).fill("rendah"), ...Array(mid).fill("sederhana"), ...Array(hi).fill("tinggi")];
    let i = 0;
    for (const sec of rules.sections) {
      const base = Math.floor(sec.totalMarks / sec.questionCount);
      let extra = sec.totalMarks - base * sec.questionCount;
      for (let q = 1; q <= sec.questionCount; q++) {
        const marks = base + (extra-- > 0 ? 1 : 0);
        slots.push({ section: sec.id, number: q, marks, difficulty: diffs[i]!, topic: topicAt(i) });
        i++;
      }
    }
    return { title: `UASA Matematik Tahun ${s.year}`, totalMarks: rules.totalMarks, durationMinutes: rules.durationMinutes, slots };
  }
  const n = s.questionCount ?? 10;
  if (!Number.isInteger(n) || n < 5 || n > 30) throw new RangeError("Bilangan soalan mesti 5–30");
  const [lo, mid, hi] = expectedDifficultyCounts(s.difficultyRatio ?? [5, 3, 2], n);
  const diffs: Difficulty[] = [...Array(lo).fill("rendah"), ...Array(mid).fill("sederhana"), ...Array(hi).fill("tinggi")];
  for (let q = 1; q <= n; q++) {
    const d = diffs[q - 1]!;
    slots.push({ section: "A", number: q, marks: d === "tinggi" ? 3 : 2, difficulty: d, topic: topicAt(q - 1) });
  }
  return {
    title: `Latihan Matematik Tahun ${s.year}`,
    totalMarks: slots.reduce((a, x) => a + x.marks, 0),
    durationMinutes: null,
    slots,
  };
}

/** A question as returned by the AI, before validation. */
export interface AiQuestion extends GeneratedQuestion {
  /** Optional machine check: arithmetic expression whose value must equal the numeric answer. */
  check?: string;
  working?: string;
  /** Objective questions: exactly four options, answer is the letter A–D. */
  type?: QuestionType;
  options?: string[];
  optionsEn?: string[];
  /** Standard Pembelajaran this question assesses (copied from its blueprint slot). */
  spCode?: string;
}

const LETTERS = ["A", "B", "C", "D"] as const;

/** "B", "b", "(B)", "B. 24" → 1. Returns -1 when the answer is not a single option letter. */
export function optionIndex(answer: string): number {
  const m = /^\(?([A-Da-d])\)?(?:[.):\s]|$)/.exec(answer.trim());
  return m ? LETTERS.indexOf(m[1]!.toUpperCase() as (typeof LETTERS)[number]) : -1;
}

/** Copy SP code and type from the blueprint onto each question, so tagging never depends on the AI. */
export function tagWithBlueprint(bp: PaperBlueprint, qs: AiQuestion[]): AiQuestion[] {
  return qs.map((q) => {
    const slot = bp.slots.find((x) => x.section === q.section && x.number === q.number);
    if (!slot) return q;
    return { ...q, ...(slot.spCode ? { spCode: slot.spCode } : {}), ...(slot.type ? { type: slot.type } : {}), topicId: slot.topic };
  });
}

/** Validate an AI paper against its blueprint plus per-question arithmetic checks. */
export function validatePaper(bp: PaperBlueprint, qs: readonly AiQuestion[], rules: UasaFormatRules | null): ValidationIssue[] {
  const issues: ValidationIssue[] = rules ? validateOfficialPaper(rules, qs) : [];
  if (qs.length !== bp.slots.length) issues.push({ code: "count", message: `Dijangka ${bp.slots.length} soalan, dapat ${qs.length}` });
  for (const slot of bp.slots) {
    const q = qs.find((x) => x.section === slot.section && x.number === slot.number);
    if (!q) {
      issues.push({ code: "missing", message: `Soalan ${slot.section}${slot.number} tiada` });
      continue;
    }
    if (q.marks !== slot.marks) issues.push({ code: "slot_marks", message: `Soalan ${slot.section}${slot.number}: markah ${q.marks}, dijangka ${slot.marks}` });
    if (!rules) {
      if (!q.stemMs?.trim()) issues.push({ code: "empty_stem", message: `Soalan ${slot.section}${slot.number}: soalan kosong` });
      if (!q.answer?.trim()) issues.push({ code: "empty_answer", message: `Soalan ${slot.section}${slot.number}: tiada jawapan` });
    }
    let numericAnswer = q.answer;
    if (slot.type === "objektif") {
      const opts = q.options ?? [];
      const clean = opts.map((o) => String(o ?? "").trim());
      if (clean.length !== 4 || clean.some((o) => !o)) {
        issues.push({ code: "options", message: `Soalan ${slot.section}${slot.number}: perlu tepat 4 pilihan jawapan A–D` });
      } else if (new Set(clean.map((o) => o.toLowerCase())).size !== 4) {
        issues.push({ code: "options_duplicate", message: `Soalan ${slot.section}${slot.number}: pilihan jawapan berulang` });
      }
      const idx = optionIndex(q.answer ?? "");
      if (idx < 0) issues.push({ code: "objective_answer", message: `Soalan ${slot.section}${slot.number}: jawapan mesti satu huruf A–D` });
      else numericAnswer = clean[idx] ?? "";
    }
    // Worded answers ("Ya, kerana …") are not machine-checked; only numeric answers must match "check".
    if (q.check && parseNumericAnswer(numericAnswer ?? "") !== null) {
      const r = checkArithmetic(q.check, numericAnswer);
      if (!r.ok) issues.push({ code: "wrong_answer", message: `Soalan ${slot.section}${slot.number}: ${r.reason}` });
    }
  }
  return issues;
}

// ── Safe arithmetic (no eval) ────────────────────────────────────────────

/** Evaluate + − × ÷ ( ) with decimals and a/b fractions. Throws on anything else. */
export function evaluateExpression(expr: string): number {
  const src = expr.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-").replace(/,/g, "").replace(/\s+/g, "");
  if (!/^[0-9.+\-*/()]+$/.test(src)) throw new Error("ungkapan tidak sah");
  let i = 0;
  const peek = () => src[i];
  const num = (): number => {
    const m = /^\d+(\.\d+)?/.exec(src.slice(i));
    if (!m) throw new Error("nombor dijangka");
    i += m[0].length;
    return Number(m[0]);
  };
  const factor = (): number => {
    if (peek() === "-") { i++; return -factor(); }
    if (peek() === "(") {
      i++;
      const v = sum();
      if (peek() !== ")") throw new Error("kurungan tidak lengkap");
      i++;
      return v;
    }
    return num();
  };
  const term = (): number => {
    let v = factor();
    while (peek() === "*" || peek() === "/") {
      const op = src[i++];
      const r = factor();
      if (op === "/" && r === 0) throw new Error("bahagi dengan sifar");
      v = op === "*" ? v * r : v / r;
    }
    return v;
  };
  const sum = (): number => {
    let v = term();
    while (peek() === "+" || peek() === "-") v = src[i++] === "+" ? v + term() : v - term();
    return v;
  };
  const v = sum();
  if (i !== src.length) throw new Error("aksara berlebihan");
  return v;
}

/** Parse a numeric answer: "RM 12.50", "1 1/2", "3/4", "45%", "1,250", "12 cm". */
export function parseNumericAnswer(answer: string): number | null {
  // Thousands may be grouped with commas or spaces ("3 700 botol").
  let s = answer.trim().replace(/^RM\s*/i, "").replace(/,/g, "").replace(/(\d)\s(?=\d{3}(?!\d|\/))/g, "$1");
  s = s.replace(/\s*(mℓ|ml|mL|ℓ|l|L|cm²|cm³|m²|m³|cm|mm|km|m|kg|g|minit|min|jam|saat|hari|minggu|bulan|tahun|°C|°|sen|unit|biji|orang|buah|%)$/i, "");
  // Counted nouns after the number: "20 biji guli", "60 orang murid", "4 keping kad".
  const counted = /^(-?\d+(?:\.\d+)?(?:\s+\d+\/\d+)?|-?\d+\/\d+)\s+[^\d\s][^\d]*$/u.exec(s);
  if (counted) s = counted[1]!;
  const mixed = /^(-?\d+)\s+(\d+)\/(\d+)$/.exec(s);
  if (mixed) return Number(mixed[1]) + Number(mixed[2]) / Number(mixed[3]);
  const frac = /^(-?\d+)\/(\d+)$/.exec(s);
  if (frac) return Number(frac[1]) / Number(frac[2]);
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

export function checkArithmetic(expr: string, answer: string): { ok: true } | { ok: false; reason: string } {
  let expected: number;
  try {
    expected = evaluateExpression(expr);
  } catch (e) {
    return { ok: false, reason: `semakan tidak dapat dikira (${(e as Error).message})` };
  }
  const got = parseNumericAnswer(answer);
  if (got === null) return { ok: false, reason: `jawapan "${answer}" bukan nombor` };
  if (Math.abs(got - expected) > 1e-6 * Math.max(1, Math.abs(expected))) {
    return { ok: false, reason: `jawapan ${answer} ≠ ${round(expected)}` };
  }
  return { ok: true };
}

const round = (n: number) => Math.round(n * 1e6) / 1e6;

